import { isExecutableLanguage } from "@corca/tiptap";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { AlignmentButtons } from "@/components/doc/right-sidebar/alignment-buttons/AlignmentButtons";
import { CollapsedPopover } from "@/components/doc/right-sidebar/CollapsedPopover";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { SidebarToolbar } from "@/components/sidebar/SidebarToolbar";
import { twMerge } from "@/utils/twMerge";

import { CodeExecutor } from "./CodeExecutor";
import { CodeFontSizeSwitcher } from "./CodeFontSizeSwitcher";
import { CodeLanguageSelector } from "./CodeLanguageSelector";
import { CodeThemeSwitcher } from "./CodeThemeSwitcher";

import type { DocumentModel } from "@/models/DocumentModel";

type Props = {
  doc: DocumentModel;
  className?: string;
  isCollapsed?: boolean;
};

const Executor = observer(({ doc, isCollapsed }: { doc: DocumentModel; isCollapsed?: boolean }) => {
  const { editorState } = doc.editor;
  const { language } = editorState.codeBlockAttrs ?? {};

  if (!isExecutableLanguage(language)) return null;

  if (isCollapsed) return <CodeExecutor doc={doc} isCollapsed />;

  return (
    <>
      <Sidebar.Delimiter />
      <CodeExecutor doc={doc} />
    </>
  );
});

const Content = observer(({ doc, className }: { doc: DocumentModel; className?: string }) => {
  const { editorState } = doc.editor;
  const t = useTranslations("sidebar");

  return (
    <SidebarToolbar label={t("toolbars.code")} className={twMerge("flex-1", className)}>
      <CodeThemeSwitcher doc={doc} />
      <CodeFontSizeSwitcher doc={doc} />
      {editorState.isInlineCode && <AlignmentButtons doc={doc} />}
      <CodeLanguageSelector doc={doc} />
    </SidebarToolbar>
  );
});

export const CodeStyle = observer(function CodeStyle({ doc, className, isCollapsed }: Props) {
  const t = useTranslations("sidebar.blocks");

  if (isCollapsed) {
    return (
      <div
        role="group"
        aria-label={t("code.title")}
        className="flex flex-col gap-1 rounded-8 p-0.75 bg-gray-100 dark:bg-gray-900"
      >
        <CollapsedPopover
          icon={<Icon name="Code" />}
          tooltipTitle={t("code.title")}
          title={t("code.title")}
        >
          <Content doc={doc} className={className} />
        </CollapsedPopover>
        <Executor doc={doc} isCollapsed />
      </div>
    );
  }

  return (
    <>
      <Content doc={doc} className={className} />
      <Executor doc={doc} />
    </>
  );
});
